import { useWithDefaultToolbar } from "@components-system";
import { ActionButton } from "../buttons/ActionButton";
import { ToolbarProps } from "@components-common/types";
import { getProps } from "@components-system";
import { useUserContext } from "../../_runtime/iam/session.context";

/**
 * Functional Component: ToolbarInner
 *
 * This component renders a toolbar with two sections: left and right. Each section can contain a set of action buttons.
 * The buttons can be restricted to certain user roles.
 *
 * Props:
 * @param {ToolbarProps} props - The properties for the toolbar.
 * @param {Array} [props.left=[]] - An array of action button configurations or strings to be rendered on the left side of the toolbar.
 * @param {Array} [props.right=[]] - An array of action button configurations or strings to be rendered on the right side of the toolbar.
 *
 * Context:
 * @param {string} role - The role of the current user, derived from the user context.
 *
 * Returns:
 * @returns {JSX.Element} A toolbar component with left and right sections populated with action buttons based on the provided props.
 */
const ToolbarInner = (props: ToolbarProps) => {
  const { role } = useUserContext();
  const { left = [], right = [] } = props;
  return (
    <div className="toolbar">
      <div className="d-flex gap-2">
        {left.map((x: any, index: number) => {
          if (x.roles) {
            if (!x.roles.includes(role)) return null;
          }
          if (typeof x === "string") return <ActionButton key={x} kind={x} />;
          else return <ActionButton key={index} {...x} />;
        })}
      </div>
      <div className="space"></div>
      <div className="d-flex gap-2">
        {right.map((x: any, index: number) => {
          if (x.roles) {
            if (!x.roles.includes(role)) return null;
          }
          if (typeof x === "string") return <ActionButton key={x} kind={x} />;
          else return <ActionButton key={index} {...x} />;
        })}
      </div>
    </div>
  );
};

/**
 * Functional Component: Toolbar
 *
 * This is the Toolbar component that is used in the application. It receives the left and right buttons as props.
 * It can either receive a string or an object in the buttons array. The prop roles can also come within the object
 * to specify which roles are allowed to see the button.
 *
 * Props:
 * @param {ToolbarProps} propsParam - The initial properties for the toolbar.
 *
 * Internal Logic:
 * @param {ToolbarProps} props - The merged properties after applying default values.
 * @param {string} type - The type of the toolbar, determined from the initial or merged properties.
 *
 * Returns:
 * @returns {JSX.Element} The ToolbarInner component with merged properties.
 */
export const Toolbar = (propsParam: ToolbarProps) => {
  const props = getProps(propsParam as ToolbarProps);
  const type = (propsParam as any)?.type || (props as any)?.type || "custom";
  const mergedProps = useWithDefaultToolbar(type, props) as ToolbarProps;

  return <ToolbarInner {...mergedProps} />;
};
